import { useMemo } from 'react';
import { Layer, Source } from 'shared/map-exports';
import { HazardListItem } from 'features/hazards/hazard.types';
import { FeatureCollection, Feature, Geometry } from 'geojson';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { getHazardEventIcon } from 'features/mapping/icons';
import {
  HAZARDS_SOURCE_ID,
  HAZARD_ICONS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { history } from 'shared/history';
import { getIconStyle } from './HazardsLayer.styles';

type HazardsLayerProps = {
  hazards: HazardListItem[];
  selectedGeoEventId?: number;
  interactive?: boolean;
};

type HazardFeatureProperties = {
  id: number;
  icon: string;
  isActive: boolean;
  offsetX: number;
  offsetY: number;
  opacity: number;
  index: number;
};

const toGeojsonFeature = (
  hazard: HazardListItem,
): Feature<Geometry, HazardFeatureProperties> => {
  const { id, isActive, lat, lng, dateCreated } = hazard;
  const icon = getHazardEventIcon(hazard);
  const opacity = isActive ? 1 : 0;
  const index = isActive ? Date.parse(dateCreated) : 0;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
    properties: {
      id,
      icon: icon.name,
      isActive,
      offsetX: 0,
      offsetY: 0,
      opacity,
      index,
    },
  };
};

export const resolveHazardPath = (
  geoJsonFeatures: MapboxFeature<HazardFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const { id, opacity } = feature.properties;
  if (opacity > 0) return `/i/${id}`;
  return null;
};

export const hazardClickHandler: MapClickHandler<HazardFeatureProperties> = (
  geoJsonFeatures,
) => {
  const path = resolveHazardPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const HazardsLayer = (props: HazardsLayerProps): JSX.Element => {
  const { hazards, selectedGeoEventId = 0, interactive = true } = props;

  const geojson: FeatureCollection<Geometry, HazardFeatureProperties> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: hazards
        .map((hazard) => toGeojsonFeature(hazard))
        .filter((feature) => feature.properties.opacity > 0),
    }),
    [hazards],
  );

  const iconStyle = getIconStyle(selectedGeoEventId);

  return (
    <Source id={HAZARDS_SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={HAZARD_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...iconStyle}
        metadata={{ interactive }}
      />
    </Source>
  );
};
